import { ReactNode, HTMLProps, isValidElement, useMemo, useState } from 'react';

// Components
import { Alert, AlertProps, Collapse } from '@mui/material';
import { IconX, IconChevronDown, IconChevronUp } from '@u21/tabler-icons';
import {
  U21Button,
  U21ButtonProps,
} from 'app/shared/u21-ui/components/input/U21Button';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';

// Utils
import { getDOMProps } from 'app/shared/utils/react';
import styled, { css } from 'styled-components';
import { useTheme } from '@mui/styles';
import { getA11yClickProps } from 'app/shared/utils/a11y';
import { ColorSchema } from 'vendor/material-minimal/palette';
import { alpha } from '@mui/system/colorManipulator';
import { U21Themes } from 'vendor/material-minimal/models';

export type U21AlertColor = 'normal' | ColorSchema;

export interface U21AlertProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'size' | 'title'
> {
  children: NonNullable<ReactNode>;
  closeButtonProps?: Omit<
    U21ButtonProps,
    'children' | 'color' | 'onClick' | 'small'
  >;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  hidden?: boolean;
  Icon?: (props: { size: number }) => ReactNode;
  onClose?: () => void;
  color?: U21AlertColor;
  title?: ReactNode;
}

const ALERT_COLOR_TO_SEVERITY: Record<U21AlertColor, AlertProps['severity']> = {
  primary: 'info',
  info: 'info',
  success: 'success',
  normal: 'info',
  warning: 'warning',
  error: 'error',
  special: 'info',
};

export const U21Alert = ({
  children,
  closeButtonProps,
  collapsible = false,
  defaultCollapsed = false,
  hidden = false,
  Icon,
  onClose,
  color = 'error',
  title,
  ...rest
}: U21AlertProps) => {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const displayTitle = useMemo(() => {
    if (!title) {
      return null;
    }
    const themedShade =
      theme.palette.mode === U21Themes.LIGHT ? 'darker' : 'lighter';
    const innerTitle = isValidElement(title) ? (
      title
    ) : (
      <U21Typography
        color={color === 'normal' ? 'text.primary' : `${color}.${themedShade}`}
        variant="subtitle2"
      >
        {title}
      </U21Typography>
    );
    if (collapsible) {
      return (
        <CollapsibleContainer
          {...getA11yClickProps(() => {
            setCollapsed((old) => !old);
          })}
        >
          <TitleContainer>{innerTitle}</TitleContainer>
          {collapsed ? <StyledIconChevronDown /> : <StyledIconChevronUp />}
        </CollapsibleContainer>
      );
    }
    return innerTitle;
  }, [collapsed, collapsible, color, theme.palette.mode, title]);

  return (
    <Collapse in={!hidden} mountOnEnter unmountOnExit {...getDOMProps(rest)}>
      <StyledAlert
        $color={color}
        severity={ALERT_COLOR_TO_SEVERITY[color]}
        icon={Icon ? <Icon size={24} /> : undefined}
        action={
          onClose && (
            <U21Button
              onClick={onClose}
              color={color === 'normal' ? 'inherit' : color}
              size="small"
              aria-label="close"
              {...closeButtonProps}
            >
              <IconX />
            </U21Button>
          )
        }
      >
        {displayTitle}
        <Collapse in={!collapsed} mountOnEnter unmountOnExit>
          {children}
        </Collapse>
      </StyledAlert>
    </Collapse>
  );
};

const StyledAlert = styled(Alert)<{ $color: U21AlertColor }>`
  ${(props) => {
    if (props.$color === 'normal') {
      return css`
        background-color: ${props.theme.palette.background.neutral};
      `;
    }

    if (props.$color === 'primary') {
      return css`
        background-color: ${props.theme.palette.mode === U21Themes.LIGHT
          ? props.theme.palette.primary.lighter
          : props.theme.palette.primary.darker};
      `;
    }

    if (props.$color === 'special') {
      return css`
        background-color: ${props.theme.palette.mode === U21Themes.LIGHT
          ? alpha(props.theme.palette.special.main, 0.08)
          : alpha(props.theme.palette.special.main, 0.48)};
      `;
    }

    return css``;
  }}

  .MuiAlert-icon {
    color: ${(props) => {
      if (props.$color === 'normal') {
        return props.theme.palette.text.primary;
      }
      if (props.$color === 'special') {
        return props.theme.palette.mode === U21Themes.LIGHT
          ? props.theme.palette.special.darker
          : props.theme.palette.special.lighter;
      }
      return props.theme.palette[props.$color].main;
    }};
  }

  .MuiAlert-message {
    flex: 1;
    ${(props) => {
      if (props.$color === 'normal') {
        return css`
          color: ${props.theme.palette.text.primary};
        `;
      }

      if (props.$color === 'primary') {
        return css`
          color: ${props.theme.palette.mode === U21Themes.LIGHT
            ? props.theme.palette.primary.darker
            : props.theme.palette.primary.lighter};
        `;
      }

      if (props.$color === 'special') {
        return css`
          color: ${props.theme.palette.mode === U21Themes.LIGHT
            ? props.theme.palette.special.darker
            : props.theme.palette.special.lighter};
        `;
      }

      return css``;
    }}
  }
`;

const CollapsibleContainer = styled.div`
  cursor: pointer;
  display: flex;
`;

const TitleContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;

const StyledIconChevronUp = styled(IconChevronUp)`
  margin-left: auto;
`;

const StyledIconChevronDown = styled(IconChevronDown)`
  margin-left: auto;
`;
